import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useFormik } from 'formik';
import PropTypes from 'prop-types';
import { MenuItem } from '@material-ui/core';
import * as yup from 'yup';
import Placeholder from '../common/Placeholder';
import CreateTaskDialog from './CreateTaskDialog';
import {
  ENTITY_TYPE_FILE,
  TASK_CONVERTER_KION_SDL_EXPORT,
  TASK_CONVERTER_KION_SDL_IMPORT,
} from '../../core/entities';
import {
  createKionSdlImportTask,
  createKionSdlExportTask,
} from '../../redux/actions/taskActions';
import { applySearch, parsePath } from '../../core/services/fileStorageService';
import { checkFileExist, getObjectsFromPath, getPathEntity } from '../../redux/actions/fileStorageActions';
import { KION_SDL_ALLOWED_OBJECT_DOMAINS, TASKS_ROUTE } from '../../core/constants';
import { getCorrectPath } from '../common/Utility';

const STEP_FIELD_TYPE_MULTIPLE = 'multiple';
const STEP_FIELD_TYPE_PATH = 'path';

const TYPES = {
  Export: TASK_CONVERTER_KION_SDL_EXPORT,
  Import: TASK_CONVERTER_KION_SDL_IMPORT,
};

function filterObjects(objects, searchValue) {
  return applySearch(objects, searchValue).sort((a, b) => b.type.localeCompare(a.type));
}

const validationSchema = taskType => {
  let schema = {
    folderRef: yup
      .number().typeError('Remote folder value must be integer')
      .integer('Remote folder value must be integer')
      .required('Remote folder is required'),
    version: yup
      .number().typeError('Version value must be integer')
      .integer('Version value must be integer')
      .required('Version is required'),
  };

  if (taskType === TASK_CONVERTER_KION_SDL_IMPORT) {
    const taskImportSchema = {
      sourceFilePath: yup.string()
        .required('Source file path is required')
        .test(
          'start_end_with_space',
          'The element of the source file path can not start or end with spaces',
          function (v) {
            const value = getCorrectPath(v, this.parent.sourceFilePathSearchValue);

            let result = true;
            value.split('/').forEach(path => {
              if (path.startsWith(' ') || path.endsWith(' ')) {
                result = false;
              }
            });

            return result;
          },
        )
        .test(
          'allowed-characters',
        <>
          Source file name can contain only the next characters:
          <br />
          {'0-9, a-z, A-Z, space, \'.\', \'-\', \'_\' and \'/\'.'}
        </>,
        function (v) {
          const { sourceFilePathSearchValue } = this.parent;
          const value = `${v}/${sourceFilePathSearchValue}`;

          return /^[a-zA-Z0-9\s/\-._]+$/.test(value);
        },
        )
        .test(
          'not-new-lines',
          'Source file name cannot contain the new lines',
          function (v) {
            const { sourceFilePathSearchValue } = this.parent;
            const value = `${v}/${sourceFilePathSearchValue}`;

            return value && !value.includes('\n');
          },
        )
        .test(
          'file-exist',
          'Source file does not exist or is not a file entity',
          async function (v) {
            const { sourceFilePathSearchValue } = this.parent;
            const value = `${v}/${sourceFilePathSearchValue}`;

            if (value) {
              const response = await getPathEntity(value);

              return response.status === 200 && response.data.data.type === ENTITY_TYPE_FILE;
            }

            return false;
          },
        ),
    };

    schema = Object.assign(schema, taskImportSchema);
  }

  if (taskType === TASK_CONVERTER_KION_SDL_EXPORT) {
    const taskExportSchema = {
      outputFilename: yup.string()
        .required('Output file name is required')
        .test(
          'start_end_with_space',
          'The output filename can not start or end with spaces',
          v => !(v.startsWith(' ') || v.endsWith(' ')),
        )
        .test(
          'allowed-characters',
        <>
          Output file name can contain only the next characters:
          <br />
          {'0-9, a-z, A-Z, space, \'.\', \'-\', \'_\' and \'/\'.'}
        </>,
        v => /^[a-zA-Z0-9\s/\-._]+$/.test(v),
        )
        .test('not-new-lines', 'Output file name cannot contain the new lines', v => v && !v.includes('\n'))
        .test('is-zip', "Output file name should end with '.zip'", v => v && v.endsWith('.zip'))
        .test('not-starts-with-slash', "Output file name cannot contain '/'", v => v && !v.includes('/'))
        .test('is-file', 'Output file name cannot consist of an extension only', v => v && v !== '.zip'),
      outputFolderPath: yup.string()
        .test(
          'required',
          'Output folder path is required',
          function (v) {
            const value = getCorrectPath(v, this.parent.outputFolderPathSearchValue);

            return value && value !== '';
          },
        )
        .test(
          'start_end_with_space',
          'The element of the output folder path can not start or end with spaces',
          function (v) {
            const value = getCorrectPath(v, this.parent.outputFolderPathSearchValue);

            let result = true;
            value.split('/').forEach(path => {
              if (path.startsWith(' ') || path.endsWith(' ')) {
                result = false;
              }
            });

            return result;
          },
        )
        .test(
          'allowed-characters',
        <>
          Output folder can contain only the next characters:
          <br />
          {'0-9, a-z, A-Z, space, \'.\', \'-\', \'_\' and \'/\'.'}
        </>,
        function (v) {
          const value = getCorrectPath(v, this.parent.outputFolderPathSearchValue);

          return /^[a-zA-Z0-9\s/\-._]+$/.test(value);
        },
        )
        .test(
          'not-new-lines',
          'Output folder name cannot contain the new lines',
          function (v) {
            const value = getCorrectPath(v, this.parent.outputFolderPathSearchValue);

            return value && !value.includes('\n');
          },
        )
        .test(
          'not-contain-extension',
          'Output folder name cannot contain the extensions',
          function (v) {
            const value = getCorrectPath(v, this.parent.outputFolderPathSearchValue);

            return value && !value.includes('.zip');
          },
        )
        .test(
          'folder-exist',
          'Output folder does not exist or is not a folder entity',
          async function (v) {
            const value = getCorrectPath(v, this.parent.outputFolderPathSearchValue);

            if (value) {
              const parentResponse = await getPathEntity(value.split('/')[0]);
              if (!(
                parentResponse.status === 200
                  && parentResponse.data.data.type !== ENTITY_TYPE_FILE
              )
              ) {
                return this.createError({ message: 'Output parent folder does not exist' });
              }

              const valueResponse = await getPathEntity(value);

              return (
                valueResponse.status === 200 && valueResponse.data.data.type !== ENTITY_TYPE_FILE
              );
            }

            return false;
          },
        ),
    };

    schema = Object.assign(schema, taskExportSchema);
  }

  return yup.object(schema);
};

const generateInitValues = task => {
  const config = JSON.parse(task.config || '{}');

  const [sourceFilePath, sourceFilename] = parsePath(task.source || '');
  const [outputFolderPath, outputFilename] = parsePath(task.output || '');

  return {
    password: '',
    version: config.version || '',
    server: config.server_ip || '',
    username: config.username || '',
    folderRef: config.folder_ref || '',
    language: config.language || 'EN',

    // sdl import
    objectDomain: config.object_domain || KION_SDL_ALLOWED_OBJECT_DOMAINS[0],
    sourceFilePath: sourceFilePath || '',
    sourceFilePathSearchValue: sourceFilename || '',
    sourceFilePathObjects: [],

    // sdl export
    outputFilename: outputFilename || '',
    asyncLoading: config.async_loading || false,

    outputFolderPath,
    outputFolderPathObjects: [],
    outputFolderPathSearchValue: '',

    createFolders: false,
  };
};

function CreateKionSdlDialog(props) {
  const {
    open,
    onClose,
    rerunTask,
    resetOnClose,
  } = props;

  const dispatch = useDispatch();

  const [creatingTask, setCreatingTask] = useState(false);

  const [taskType, setTaskType] = useState(rerunTask.converter || '');

  const [outputFileWarning, setOutputFileWarning] = useState(undefined);

  const [loadingOutputFolderObjects, setLoadingOutputFolderObjects] = useState(false);
  const [loadingSourceFileObjects, setLoadingSourceFileObjects] = useState(false);

  const formik = useFormik({
    initialValues: generateInitValues(rerunTask || {}),
    validationSchema: validationSchema(taskType),
    validateOnChange: false,
    validateOnMount: false,
    validateOnBlur: false,
  });

  const handleClose = link => {
    onClose(link);

    formik.resetForm();
  };

  const onFormSubmit = values => {
    setCreatingTask(true);

    if (taskType === TASK_CONVERTER_KION_SDL_IMPORT) {
      const task = {
        source: `${values.sourceFilePath}${values.sourceFilePath.endsWith('/') ? '' : '/'}${values.sourceFilePathSearchValue}`,
        folder_ref: parseInt(values.folderRef, 10),
        object_domain: values.objectDomain,
        language: values.language,
        username: values.username,
        password: values.password,
        server_ip: values.server,
        version: values.version,
      };

      dispatch(createKionSdlImportTask(task))
        .then(res => handleClose(`${TASKS_ROUTE}/${res.id}`))
        .catch(() => setCreatingTask(false));
    }

    if (taskType === TASK_CONVERTER_KION_SDL_EXPORT) {
      let outputFolder = `${values.outputFolderPath}${values.outputFolderPath.endsWith('/') ? '' : '/'}`;
      if (values.outputFolderPathSearchValue !== '') {
        outputFolder += `${values.outputFolderPathSearchValue}${values.outputFolderPathSearchValue.endsWith('/') ? '' : '/'}`;
      }

      const task = {
        output: `${outputFolder}${values.outputFilename}`,
        folder_ref: parseInt(values.folderRef, 10),
        async_loading: values.asyncLoading,
        username: values.username,
        password: values.password,
        language: values.language,
        server_ip: values.server,
        version: values.version,
      };

      dispatch(createKionSdlExportTask(task))
        .then(res => handleClose(`${TASKS_ROUTE}/${res.id}`))
        .catch(() => setCreatingTask(false));
    }
  };

  const onCreateButtonClick = () => {
    if (creatingTask) return;
    setCreatingTask(true);

    onFormSubmit(formik.values);
  };

  useEffect(() => {
    if (open && taskType === TASK_CONVERTER_KION_SDL_EXPORT) {
      setLoadingOutputFolderObjects(true);

      const value = getCorrectPath(formik.values.outputFolderPath, '');

      getObjectsFromPath(`/${value}`)
        .then(objects => {
          formik.setFieldValue('outputFolderPathObjects', objects.sort((a, b) => b.type.localeCompare(a.type)))
            .then(() => {
              formik.validateField('outputFolderPath').then(() => setLoadingOutputFolderObjects(false));
            });
        });
    }
  }, [taskType, formik.values.outputFolderPath]);

  useEffect(() => {
    if (open && taskType === TASK_CONVERTER_KION_SDL_IMPORT) {
      setLoadingSourceFileObjects(true);

      getObjectsFromPath(`/${formik.values.sourceFilePath}`)
        .then(objects => {
          formik.setFieldValue('sourceFilePathObjects', objects.sort((a, b) => b.type.localeCompare(a.type)))
            .then(() => {
              formik.validateField('sourceFilePath').then(() => setLoadingSourceFileObjects(false));
            });
        });
    }
  }, [taskType, formik.values.sourceFilePath]);

  const onChangePathValue = (value, fieldName, searchFieldName) => {
    const split = value.split('/');

    const search = split[split.length - 1];
    const path = `${split.slice(0, -1).join('/')}`;

    if (path !== '/' || formik.values[fieldName] !== path) formik.setFieldValue(fieldName, path);
    formik.setFieldValue(searchFieldName, search).then(() => formik.validateField(fieldName));
  };

  let outputFolder = `${formik.values.outputFolderPath}${formik.values.outputFolderPath.endsWith('/') ? '' : '/'}`;
  if (formik.values.outputFolderPathSearchValue !== '') {
    outputFolder += `${formik.values.outputFolderPathSearchValue}${formik.values.outputFolderPathSearchValue.endsWith('/') ? '' : '/'}`;
  }

  const config = {
    title: 'Create Kion Tridion',
    steps: [
      {
        isValid: formik.values.username !== '' && formik.values.password !== ''
          && formik.values.server !== '' && taskType !== '',
        type: STEP_FIELD_TYPE_MULTIPLE,
        title: 'General settings',
        fields: [
          {
            tooltip: (
              <>
                <p>The Tridion server&apos;s IP address.</p>
                <p>20.76.196.145 is a development server.</p>
                <p>104.46.49.223 is a testing server.</p>
                <p>23.97.130.54 is a production server.</p>
              </>
            ),
            onChange: e => formik.handleChange(e),
            value: formik.values.server,
            error: formik.errors.server,
            placeholder: 'Server',
            label: 'Server',
            key: 'server',
          },
          {
            onChange: e => formik.handleChange(e),
            tooltip: 'The name of the user for the Tridion server.',
            value: formik.values.username,
            error: formik.errors.username,
            placeholder: 'Username',
            label: 'Username',
            key: 'username',
          },
          {
            onChange: e => formik.handleChange(e),
            tooltip: 'Password for the user for the Tridion server.',
            value: formik.values.password,
            error: formik.errors.password,
            placeholder: 'Password',
            label: 'Password',
            type: 'password',
            key: 'password',
          },
          {
            tooltip: (
              <>
                <p>Export allows you to download files from the specified folder.</p>
                <p>Import allows you to upload files to the specified folder.</p>
              </>
            ),
            item: ([key, value]) => <MenuItem key={value} value={value}>{key}</MenuItem>,
            renderValue: selected => {
              if (selected === '') return <Placeholder>Task type</Placeholder>;

              let returnValue = selected;
              Object.entries(TYPES).forEach(([key, value]) => {
                if (selected === value) returnValue = key;
              });

              return returnValue;
            },
            onChange: event => setTaskType(event.target.value),
            items: Object.entries(TYPES),
            label: 'Task type',
            error: undefined,
            value: taskType,
            key: 'taskType',
          },
        ],
        onSubmit: () => Promise.resolve(true),
        allowContinue: taskType !== '',
        loading: false,
      },
      {
        type: STEP_FIELD_TYPE_MULTIPLE,
        title: 'Task details',
        fields: [],
      },
    ],
    onSubmit: () => onCreateButtonClick(),
    loading: creatingTask,
    initActiveStep: 0,
  };

  const fields = [
    {
      onChange: e => formik.handleChange(e),
      tooltip: 'The version of the objects for the Tridion server.',
      value: formik.values.version,
      error: formik.errors.version,
      placeholder: 'Version',
      label: 'Version',
      key: 'version',
    },
    {
      item: lang => <MenuItem key={lang} value={lang}>{lang}</MenuItem>,
      tooltip: 'The (working) language code of the object for the Tridion server. For example, EN, DE, etc.',
      onChange: e => formik.handleChange(e),
      value: formik.values.language,
      error: formik.errors.language,
      placeholder: 'Language',
      label: 'Language',
      key: 'language',
    },
    {
      tooltip: 'The folder identifier on the Tridion repository.',
      onChange: e => formik.handleChange(e),
      value: formik.values.folderRef,
      error: formik.errors.folderRef,
      placeholder: 'Remote folder',
      label: 'Remote folder',
      key: 'folderRef',
    },
  ];

  if (taskType === TASK_CONVERTER_KION_SDL_IMPORT) {
    fields.push({
      tooltip: 'The object domain is required for the Tridion server.',
      item: obj => <MenuItem key={obj} value={obj}>{obj}</MenuItem>,
      items: KION_SDL_ALLOWED_OBJECT_DOMAINS,
      onChange: e => formik.handleChange(e),
      value: formik.values.objectDomain,
      error: formik.errors.objectDomain,
      placeholder: 'Object domain',
      label: 'Object domain',
      key: 'objectDomain',
    });

    config.steps[1] = {
      type: STEP_FIELD_TYPE_MULTIPLE,
      title: 'Task details',
      fields,
      isValid: formik.values.version !== '' && formik.values.folderRef !== '' && formik.errors.folderRef === undefined && formik.values.objectDomain !== '' && formik.values.language !== '',
      onSubmit: () => Promise.resolve(true),
      allowContinue: true,
      loading: false,
    };

    config.steps.push({
      title: 'Source file path',
      type: STEP_FIELD_TYPE_PATH,
      pathField: {
        objects: filterObjects(
          formik.values.sourceFilePathObjects, formik.values.sourceFilePathSearchValue,
        ),
        onChange: value => onChangePathValue(value, 'sourceFilePath', 'sourceFilePathSearchValue'),
        value: formik.values.sourceFilePath !== ''
          ? `${formik.values.sourceFilePath}${formik.values.sourceFilePath.endsWith('/') ? '' : '/'}${formik.values.sourceFilePathSearchValue}`
          : formik.values.sourceFilePathSearchValue,
        error: formik.errors.sourceFilePath,
        loading: false,
      },
      allowContinue: formik.values.sourceFilePath !== '',
      isValid: formik.errors.sourceFilePath === undefined,
      onSubmit: () => Promise.resolve(true),
      loading: loadingSourceFileObjects,
    });
  }

  if (taskType === TASK_CONVERTER_KION_SDL_EXPORT) {
    let allowCreateFolders = false;
    if (formik.errors.outputFolderPath !== undefined) {
      if (formik.errors.outputFolderPath === 'Output folder does not exist or is not a folder entity') {
        allowCreateFolders = true;
      }
    }

    config.steps[1] = {
      type: STEP_FIELD_TYPE_MULTIPLE,
      title: 'Task details',
      fields,
      isValid: (
        formik.values.version !== '' && formik.values.folderRef !== ''
        && formik.errors.folderRef === undefined && formik.values.language !== ''
      ),
      onSubmit: () => Promise.resolve(true),
      configurations: [
        {
          tooltip: 'The files will be loaded in the few processes.',
          onChange: event => formik.handleChange(event),
          value: formik.values.asyncLoading,
          label: 'Async loading',
          key: 'asyncLoading',
        },
      ],
      allowContinue: true,
      loading: false,
    };

    config.steps.push({
      title: 'Output file path',
      type: STEP_FIELD_TYPE_PATH,
      fields: [
        {
          onChange: event => formik.handleChange(event),
          value: formik.values.outputFilename,
          error: formik.errors.outputFilename,
          placeholder: 'Example.zip',
          label: 'Output file name',
          key: 'outputFilename',
        },
      ],
      pathField: {
        label: 'Output folder path',
        placeholder: 'Search a folder by name',
        objects: filterObjects(
          formik.values.outputFolderPathObjects, formik.values.outputFolderPathSearchValue,
        ).filter(o => o.type === 'folder'),
        onChange: value => {
          onChangePathValue(value, 'outputFolderPath', 'outputFolderPathSearchValue');
          formik.setFieldValue('createFolders', false);
        },
        value: formik.values.outputFolderPath !== ''
          ? `${formik.values.outputFolderPath}${formik.values.outputFolderPath.endsWith('/') ? '' : '/'}${formik.values.outputFolderPathSearchValue}`
          : formik.values.outputFolderPathSearchValue,
      },
      configurations: allowCreateFolders ? [
        {
          tooltip: (
            <p>
              <p>
                The output folder does not exist. If you select this parameter,
                the folder will be created automatically.
              </p>
              <p>
                <b>Notice. </b>
                Use this parameter carefully.
              </p>
              <p>
                THE PARENT FOLDER MUST EXIST.
              </p>
            </p>
          ),
          onChange: event => formik.handleChange(event),
          value: formik.values.createFolders,
          label: 'Create Folders',
          key: 'createFolders',
        },
      ] : [],
      allowContinue: formik.values.outputFolderPath !== '' && formik.values.outputFilename !== ''
        && (formik.errors.outputFolderPath === undefined || formik.values.createFolders),
      isValid: formik.values.outputFolderPath !== ''
        && (formik.values.outputFilename !== '' && formik.errors.outputFilename === undefined),
      onSubmit: () => Promise.resolve(true),
      error: formik.errors.outputFolderPath,
      loading: loadingOutputFolderObjects,
      warning: outputFileWarning,
    });
  }

  useEffect(() => {
    const fileId = `${outputFolder}${formik.values.outputFilename}`;

    if (formik.values.outputFolderPath === outputFolder) {
      const exist = formik.values.outputFolderPathObjects.some(file => file.id === fileId);

      if (exist) setOutputFileWarning('The output file already exists');
      else setOutputFileWarning(undefined);
    } else {
      dispatch(checkFileExist(fileId))
        .then(() => setOutputFileWarning('The output file already exists'))
        .catch(() => setOutputFileWarning(undefined));
    }
  }, [
    formik.values.outputFolderPath,
    formik.values.outputFolderPathSearchValue,
    formik.values.outputFolderPathObjects,
    formik.values.outputFilename,
  ]);

  useEffect(() => {
    if (open && formik.values.folderRef !== '') formik.validateField('folderRef');
  }, [formik.values.folderRef]);

  useEffect(() => {
    if (open && formik.values.outputFilename !== '') formik.validateField('outputFilename');
  }, [formik.values.outputFilename]);

  return (
    <>
      <CreateTaskDialog
        open={open}
        config={config}
        onClose={() => {
          onClose();

          if (resetOnClose) {
            setTaskType('');
            formik.setValues(generateInitValues({}));
          }
        }}
        resetOnClose={resetOnClose}
      />
    </>
  );
}

CreateKionSdlDialog.defaultProps = { rerunTask: {}, resetOnClose: true };

CreateKionSdlDialog.propTypes = {
  onClose: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  resetOnClose: PropTypes.bool,
  rerunTask: PropTypes.object,
};

export default CreateKionSdlDialog;
